import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  query, 
  where,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  Permission,
  RolePermissions,
  PermissionCategory,
  PermissionCheckResult,
  PermissionChangeLog,
  PermissionTemplate,
  DataAccessLevel,
  MenuAccess,
  HierarchicalPermission,
  UserCustomPermission,
  UserEffectivePermissions,
  StoragePermissions,
  DEFAULT_PERMISSION_CATEGORIES,
  PERMISSION_RESOURCES,
  PERMISSION_ACTIONS
} from '../../types/permission.types';
import { UserRole, User } from '../../types/user.types';
import { DEFAULT_HIERARCHICAL_PERMISSIONS } from '../../config/menuPermissionConfig';
import { DEFAULT_STORAGE_PERMISSIONS } from '../../config/permissionAudit';

// Collection references
const PERMISSIONS_COLLECTION = 'permissions';
const ROLE_PERMISSIONS_COLLECTION = 'rolePermissions';
const PERMISSION_LOGS_COLLECTION = 'permissionLogs';
const PERMISSION_TEMPLATES_COLLECTION = 'permissionTemplates';
const USER_CUSTOM_PERMISSIONS_COLLECTION = 'userCustomPermissions';

// Cache for permissions
let permissionsCache: Map<string, Permission> = new Map();
let rolePermissionsCache: Map<string, RolePermissions> = new Map();
let cacheLastUpdated: Date | null = null;
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

// Initialize default permissions
export const initializeDefaultPermissions = async (): Promise<void> => {
  const batch = writeBatch(db);
  
  // Create default permissions for each resource and action combination
  const resources = Object.values(PERMISSION_RESOURCES);
  const actions = Object.values(PERMISSION_ACTIONS);
  
  for (const resource of resources) {
    for (const action of actions) {
      // Skip certain combinations that don't make sense
      if (
        (resource === 'activities' && ['create', 'update', 'delete'].includes(action)) ||
        (resource === 'reports' && ['create', 'update', 'delete'].includes(action))
      ) {
        continue;
      }
      
      const permissionId = `${resource}.${action}`;
      const categoryId = getCategoryForResource(resource);
      
      const permission: Permission = {
        id: permissionId,
        categoryId,
        name: permissionId,
        displayName: `${capitalizeFirst(action)} ${capitalizeFirst(resource)}`,
        description: `Permission to ${action} ${resource}`,
        resource,
        action,
        isSystemPermission: isSystemPermission(resource, action),
        dependencies: getPermissionDependencies(resource, action)
      };
      
      const docRef = doc(db, PERMISSIONS_COLLECTION, permissionId);
      batch.set(docRef, {
        ...permission,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
    }
  }
  
  await batch.commit();
};

// Get all permissions
export const getAllPermissions = async (forceRefresh = false): Promise<Permission[]> => {
  if (!forceRefresh && cacheLastUpdated && 
      Date.now() - cacheLastUpdated.getTime() < CACHE_DURATION_MS &&
      permissionsCache.size > 0) {
    return Array.from(permissionsCache.values());
  }
  
  const snapshot = await getDocs(collection(db, PERMISSIONS_COLLECTION));
  const permissions: Permission[] = [];
  
  snapshot.forEach(doc => {
    const permission = { id: doc.id, ...doc.data() } as Permission;
    permissions.push(permission);
    permissionsCache.set(permission.id, permission);
  });
  
  cacheLastUpdated = new Date();
  return permissions;
};

// Get permissions by category
export const getPermissionsByCategory = async (categoryId: string): Promise<Permission[]> => {
  const allPermissions = await getAllPermissions();
  return allPermissions.filter(p => p.categoryId === categoryId);
};

// Get role permissions
export const getRolePermissions = async (roleId: UserRole): Promise<RolePermissions | null> => {
  // Check cache first
  if (rolePermissionsCache.has(roleId)) {
    return rolePermissionsCache.get(roleId) || null;
  }

  const docRef = doc(db, ROLE_PERMISSIONS_COLLECTION, roleId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    // Fall back to DEFAULT_HIERARCHICAL_PERMISSIONS if no Firestore config exists
    const defaultPerms = DEFAULT_HIERARCHICAL_PERMISSIONS[roleId];
    if (defaultPerms) {
      console.log(`[permissionService] Using DEFAULT_HIERARCHICAL_PERMISSIONS for role: ${roleId}`);
      const rolePermissions: RolePermissions = {
        roleId,
        permissions: [],
        hierarchicalPermissions: defaultPerms as HierarchicalPermission,
        menuAccess: [],
        customSettings: {
          dataAccessLevel: DataAccessLevel.OWN,
          canExport: false,
          canBulkEdit: false
        }
      };
      rolePermissionsCache.set(roleId, rolePermissions);
      return rolePermissions;
    }
    return null;
  }

  const rolePermissions = { id: docSnap.id, ...docSnap.data() } as RolePermissions;
  rolePermissionsCache.set(roleId, rolePermissions);

  return rolePermissions;
};

// Update role permissions (overloaded)
export async function updateRolePermissions(
  roleId: UserRole,
  permissions: Permission[]
): Promise<void>;
export async function updateRolePermissions(
  roleId: UserRole,
  permissions: string[],
  menuAccess?: MenuAccess[],
  updatedBy?: string,
  hierarchicalPermissions?: HierarchicalPermission
): Promise<void>;
export async function updateRolePermissions(
  roleId: UserRole,
  permissions: string[] | Permission[],
  menuAccess?: MenuAccess[],
  updatedBy?: string,
  hierarchicalPermissions?: HierarchicalPermission
): Promise<void> {
  const existingPermissions = await getRolePermissions(roleId);
  
  // Convert Permission[] to string[] if needed
  const permissionIds = typeof permissions[0] === 'object' 
    ? (permissions as Permission[]).map(p => p.id)
    : permissions as string[];
  
  // Use existing menuAccess if not provided
  const finalMenuAccess = menuAccess || existingPermissions?.menuAccess || [];
  
  // Create or update role permissions
  const rolePermissionsData: RolePermissions = {
    roleId,
    permissions: permissionIds,
    hierarchicalPermissions: hierarchicalPermissions || existingPermissions?.hierarchicalPermissions,
    menuAccess: finalMenuAccess,
    customSettings: existingPermissions?.customSettings || {
      dataAccessLevel: DataAccessLevel.OWN,
      canExport: false,
      canBulkEdit: false,
      canDeletePermanently: false
    },
    updatedAt: Timestamp.now()
  };
  
  if (!existingPermissions) {
    rolePermissionsData.createdAt = Timestamp.now();
  }
  
  const docRef = doc(db, ROLE_PERMISSIONS_COLLECTION, roleId);
  await setDoc(docRef, rolePermissionsData);
  
  // Log the change if updatedBy is provided
  if (updatedBy) {
    await logPermissionChange(
      roleId,
      updatedBy,
      existingPermissions?.permissions || [],
      permissionIds
    );
  }
  
  // Clear cache
  rolePermissionsCache.delete(roleId);
};

// Check if user has permission
export const checkPermission = async (
  userRole: UserRole,
  permissionId: string
): Promise<PermissionCheckResult> => {
  const rolePermissions = await getRolePermissions(userRole);
  
  if (!rolePermissions) {
    return {
      hasPermission: false,
      reason: 'Role permissions not configured',
      requiredPermissions: [permissionId]
    };
  }
  
  // Super users always have all permissions
  if (userRole === UserRole.PROPCLOUDAI_SUPER_USER) {
    return { hasPermission: true };
  }
  
  // Check if role has the permission
  if (rolePermissions.permissions.includes(permissionId)) {
    return { hasPermission: true };
  }
  
  // Check if permission has dependencies
  const permission = permissionsCache.get(permissionId);
  if (permission?.dependencies) {
    const missingDeps = permission.dependencies.filter(
      dep => !rolePermissions.permissions.includes(dep)
    );
    
    if (missingDeps.length > 0) {
      return {
        hasPermission: false,
        reason: 'Missing required dependencies',
        requiredPermissions: missingDeps
      };
    }
  }
  
  return {
    hasPermission: false,
    reason: 'Permission not granted to role',
    requiredPermissions: [permissionId]
  };
};

// Check multiple permissions (AND logic)
export const checkPermissions = async (
  userRole: UserRole,
  permissionIds: string[]
): Promise<PermissionCheckResult> => {
  const missingPermissions: string[] = [];
  
  for (const permissionId of permissionIds) {
    const result = await checkPermission(userRole, permissionId);
    if (!result.hasPermission) {
      missingPermissions.push(...(result.requiredPermissions || [permissionId]));
    }
  }
  
  if (missingPermissions.length > 0) {
    return {
      hasPermission: false,
      reason: 'Missing required permissions',
      requiredPermissions: Array.from(new Set(missingPermissions))
    };
  }
  
  return { hasPermission: true };
};

// Check menu access
export const checkMenuAccess = async (
  userRole: UserRole,
  menuId: string
): Promise<boolean> => {
  const rolePermissions = await getRolePermissions(userRole);
  
  if (!rolePermissions) {
    return false;
  }
  
  // Super users have access to all menus
  if (userRole === UserRole.PROPCLOUDAI_SUPER_USER) {
    return true;
  }
  
  const menuAccess = rolePermissions.menuAccess.find(m => m.menuId === menuId);
  return menuAccess?.visible || false;
};

// Get permission templates
export const getPermissionTemplates = async (): Promise<PermissionTemplate[]> => {
  const snapshot = await getDocs(collection(db, PERMISSION_TEMPLATES_COLLECTION));
  const templates: PermissionTemplate[] = [];
  
  snapshot.forEach(doc => {
    templates.push({ id: doc.id, ...doc.data() } as PermissionTemplate);
  });
  
  return templates;
};

// Apply permission template to role
export const applyPermissionTemplate = async (
  roleId: UserRole,
  templateId: string,
  updatedBy: string
): Promise<void> => {
  const templateDoc = await getDoc(doc(db, PERMISSION_TEMPLATES_COLLECTION, templateId));
  
  if (!templateDoc.exists()) {
    throw new Error('Template not found');
  }
  
  const template = templateDoc.data() as PermissionTemplate;
  const existingPermissions = await getRolePermissions(roleId);
  
  // Merge template permissions with existing ones
  const mergedPermissions = [
    ...(existingPermissions?.permissions || []),
    ...template.permissions
  ];
  
  // Remove duplicates
  const uniquePermissions = Array.from(new Set(mergedPermissions));
  
  await updateRolePermissions(
    roleId,
    uniquePermissions,
    existingPermissions?.menuAccess || [],
    updatedBy
  );
};

// Log permission changes for audit
export const logPermissionChange = async (
  roleId: string,
  changedBy: string,
  previousPermissions: string[],
  newPermissions: string[]
): Promise<void> => {
  const added = newPermissions.filter(p => !previousPermissions.includes(p));
  const removed = previousPermissions.filter(p => !newPermissions.includes(p));
  
  if (added.length === 0 && removed.length === 0) {
    return; // No changes
  }
  
  const log: PermissionChangeLog = {
    roleId,
    changedBy,
    changeType: added.length > 0 && removed.length > 0 ? 'modify' : 
                added.length > 0 ? 'grant' : 'revoke',
    permissions: added.length > 0 ? added : removed,
    previousPermissions,
    timestamp: Timestamp.now()
  };
  
  await setDoc(doc(collection(db, PERMISSION_LOGS_COLLECTION)), log);
};

// Helper functions
const getCategoryForResource = (resource: string): string => {
  const categoryMap: Record<string, string> = {
    developments: 'development_management',
    users: 'user_management',
    reservations: 'sales_reservations',
    deals: 'sales_reservations',
    reports: 'financial_reports',
    contacts: 'crm_contacts',
    settings: 'system_admin',
    roles: 'system_admin',
    partners: 'partner_management',
    commissions: 'financial_reports',
    documents: 'development_management',
    activities: 'system_admin',
    invoices: 'financial_reports',
    viewings: 'development_management'
  };
  
  return categoryMap[resource] || 'system_admin';
};

const isSystemPermission = (resource: string, action: string): boolean => {
  // Define which permissions are system-level and cannot be removed from super users
  const systemPermissions = [
    'users.manage',
    'roles.manage',
    'settings.manage',
    'system.manage'
  ];
  
  return systemPermissions.includes(`${resource}.${action}`);
};

const getPermissionDependencies = (resource: string, action: string): string[] => {
  // Define permission dependencies
  const dependencies: Record<string, string[]> = {
    'developments.create': ['developments.read'],
    'developments.update': ['developments.read'],
    'developments.delete': ['developments.read', 'developments.update'],
    'users.create': ['users.read'],
    'users.update': ['users.read'],
    'users.delete': ['users.read', 'users.update'],
    'reservations.create': ['reservations.read', 'developments.read'],
    'reservations.update': ['reservations.read'],
    'reservations.delete': ['reservations.read', 'reservations.update'],
    'deals.create': ['deals.read', 'reservations.read'],
    'deals.update': ['deals.read'],
    'deals.delete': ['deals.read', 'deals.update'],
    'reports.export': ['reports.read'],
    'commissions.approve': ['commissions.read', 'commissions.update']
  };
  
  return dependencies[`${resource}.${action}`] || [];
};

const capitalizeFirst = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// Additional exports for PermissionContext
export const getUserPermissions = async (userRole: UserRole): Promise<Permission[]> => {
  const rolePermissions = await getRolePermissions(userRole);
  if (!rolePermissions) return [];
  
  const allPermissions = await getAllPermissions();
  return allPermissions.filter(p => rolePermissions.permissions.includes(p.id));
};

export const hasPermission = (permissions: Permission[], permissionId: string): boolean => {
  // Support hierarchical permission checking
  if (permissionId.startsWith('dashboardCards.')) {
    // For hierarchical permissions, check the rolePermissions hierarchicalPermissions field
    // This would need to be passed in or checked separately
    // For now, return true for super users
    return permissions.some(p => p.id === 'system.manage');
  }
  return permissions.some(p => p.id === permissionId);
};

export const hasAnyPermission = (permissions: Permission[], permissionIds: string[]): boolean => {
  return permissionIds.some(id => hasPermission(permissions, id));
};

// User Custom Permission Functions
export const getUserCustomPermissions = async (userId: string): Promise<UserCustomPermission | null> => {
  try {
    const q = query(
      collection(db, USER_CUSTOM_PERMISSIONS_COLLECTION),
      where('userId', '==', userId),
      where('isActive', '==', true)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    const doc = querySnapshot.docs[0];
    return { id: doc.id, ...doc.data() } as UserCustomPermission;
  } catch (error) {
    console.error('Error fetching user custom permissions:', error);
    return null;
  }
};

export const createUserCustomPermission = async (
  customPermission: Omit<UserCustomPermission, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> => {
  try {
    const docRef = doc(collection(db, USER_CUSTOM_PERMISSIONS_COLLECTION));
    await setDoc(docRef, {
      ...customPermission,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      isActive: true
    });

    // Update user document to flag custom permissions
    const userRef = doc(db, 'users', customPermission.userId);
    await updateDoc(userRef, {
      customPermissionId: docRef.id,
      hasCustomPermissions: true
    });

    // Log the permission change
    await logPermissionChange(
      customPermission.userId,
      customPermission.grantedBy,
      [], // No previous permissions for new custom permission
      customPermission.grantedPermissions
    );

    return docRef.id;
  } catch (error) {
    console.error('Error creating user custom permission:', error);
    throw error;
  }
};

export const updateUserCustomPermission = async (
  permissionId: string,
  updates: Partial<UserCustomPermission>
): Promise<void> => {
  try {
    const docRef = doc(db, USER_CUSTOM_PERMISSIONS_COLLECTION, permissionId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating user custom permission:', error);
    throw error;
  }
};

export const deactivateUserCustomPermission = async (
  userId: string,
  deactivatedBy: string,
  reason?: string
): Promise<void> => {
  try {
    const customPerm = await getUserCustomPermissions(userId);

    if (customPerm && customPerm.id) {
      // Deactivate the custom permission
      await updateUserCustomPermission(customPerm.id, {
        isActive: false,
        notes: reason || 'Deactivated'
      });

      // Update user document
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        customPermissionId: null,
        hasCustomPermissions: false
      });

      // Log the change
      await logPermissionChange(
        userId,
        deactivatedBy,
        customPerm.grantedPermissions,
        [] // No permissions after deactivation
      );
    }
  } catch (error) {
    console.error('Error deactivating user custom permission:', error);
    throw error;
  }
};

export const getUserEffectivePermissions = async (
  user: User
): Promise<UserEffectivePermissions> => {
  try {
    // Get role permissions
    const rolePerms = await getRolePermissions(user.role);
    const rolePermissions = rolePerms?.permissions || [];

    // Get custom permissions if exists
    const customPerms = user.hasCustomPermissions
      ? await getUserCustomPermissions(user.id)
      : null;

    const customGranted = customPerms?.grantedPermissions || [];
    const customRevoked = customPerms?.revokedPermissions || [];

    // Calculate effective permissions
    const effectivePermissions = [
      ...rolePermissions.filter(p => !customRevoked.includes(p)),
      ...customGranted
    ];

    // Merge hierarchical permissions
    const hierarchicalPermissions = customPerms?.hierarchicalOverrides ||
      rolePerms?.hierarchicalPermissions || { dashboardCards: {} };

    return {
      rolePermissions,
      customGranted,
      customRevoked,
      effectivePermissions: Array.from(new Set(effectivePermissions)),
      hierarchicalPermissions,
      source: customPerms ? 'mixed' : 'role'
    };
  } catch (error) {
    console.error('Error getting user effective permissions:', error);
    // Return role-only permissions as fallback
    const rolePerms = await getRolePermissions(user.role);
    return {
      rolePermissions: rolePerms?.permissions || [],
      customGranted: [],
      customRevoked: [],
      effectivePermissions: rolePerms?.permissions || [],
      hierarchicalPermissions: rolePerms?.hierarchicalPermissions || { dashboardCards: {} },
      source: 'role'
    };
  }
};

export const getAllUsersWithCustomPermissions = async (): Promise<UserCustomPermission[]> => {
  try {
    const q = query(
      collection(db, USER_CUSTOM_PERMISSIONS_COLLECTION),
      where('isActive', '==', true)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as UserCustomPermission));
  } catch (error) {
    console.error('Error fetching all users with custom permissions:', error);
    return [];
  }
};

export const hasAllPermissions = (permissions: Permission[], permissionIds: string[]): boolean => {
  return permissionIds.every(id => hasPermission(permissions, id));
};

// Clear all caches
export const clearPermissionCaches = (): void => {
  permissionsCache.clear();
  rolePermissionsCache.clear();
  cacheLastUpdated = null;
};

// Update hierarchical permissions for a role
export const updateRoleHierarchicalPermissions = async (
  roleId: UserRole,
  hierarchicalPermissions: HierarchicalPermission,
  customSettings?: Partial<RolePermissions['customSettings']>
): Promise<void> => {
  const existingPermissions = await getRolePermissions(roleId);

  const rolePermissionsData: RolePermissions = {
    roleId,
    permissions: existingPermissions?.permissions || [],
    hierarchicalPermissions,
    menuAccess: existingPermissions?.menuAccess || [],
    customSettings: {
      dataAccessLevel: customSettings?.dataAccessLevel || existingPermissions?.customSettings?.dataAccessLevel || DataAccessLevel.OWN,
      canExport: customSettings?.canExport ?? existingPermissions?.customSettings?.canExport ?? false,
      canBulkEdit: customSettings?.canBulkEdit ?? existingPermissions?.customSettings?.canBulkEdit ?? false,
      canDeletePermanently: customSettings?.canDeletePermanently ?? existingPermissions?.customSettings?.canDeletePermanently ?? false
    },
    updatedAt: Timestamp.now()
  };

  if (!existingPermissions) {
    rolePermissionsData.createdAt = Timestamp.now();
  }

  const docRef = doc(db, ROLE_PERMISSIONS_COLLECTION, roleId);
  await setDoc(docRef, rolePermissionsData);

  // Clear cache
  rolePermissionsCache.delete(roleId);
};

// Log permission change with structured data (alternative signature)
export interface PermissionChangeLogEntry {
  userId: string;
  action: string;
  targetType: 'role' | 'user';
  targetId: string;
  previousValue?: string;
  newValue?: string;
  reason?: string;
}

export const logPermissionChangeEntry = async (entry: PermissionChangeLogEntry): Promise<void> => {
  try {
    const logData = {
      ...entry,
      timestamp: Timestamp.now()
    };

    await setDoc(doc(collection(db, PERMISSION_LOGS_COLLECTION)), logData);
  } catch (error) {
    console.error('Error logging permission change:', error);
  }
};

// Check hierarchical permission
export const checkHierarchicalPermission = async (
  userRole: UserRole,
  permissionPath: string
): Promise<boolean> => {
  // Super users always have all permissions
  if (userRole === UserRole.PROPCLOUDAI_SUPER_USER) {
    return true;
  }

  const rolePermissions = await getRolePermissions(userRole);
  if (!rolePermissions?.hierarchicalPermissions) {
    return false;
  }

  // Parse the permission path (e.g., "dashboardCards.hq.menuGroups.development.subItems.create-development")
  const parts = permissionPath.split('.');

  if (parts[0] !== 'dashboardCards') {
    return false;
  }

  const cardId = parts[1];
  const card = rolePermissions.hierarchicalPermissions.dashboardCards[cardId];

  if (!card?.enabled) {
    return false;
  }

  if (parts.length === 2) {
    return true; // Just checking card access
  }

  if (parts[2] === 'menuGroups' && parts.length >= 4) {
    const groupId = parts[3];
    const group = card.menuGroups[groupId];

    if (!group?.enabled) {
      return false;
    }

    if (parts.length === 4) {
      return true; // Just checking group access
    }

    if (parts[4] === 'subItems' && parts.length === 6) {
      const itemId = parts[5];
      return group.subItems[itemId] === true;
    }
  }

  return false;
};

// ============================================================
// Storage Permission Functions
// ============================================================

/**
 * Get storage permissions for a role
 * Falls back to DEFAULT_STORAGE_PERMISSIONS if not in Firestore
 */
export const getRoleStoragePermissions = async (
  roleId: UserRole
): Promise<StoragePermissions | null> => {
  try {
    const rolePermissions = await getRolePermissions(roleId);

    // Use stored permissions if available
    if (rolePermissions?.storagePermissions) {
      return rolePermissions.storagePermissions;
    }

    // Fall back to default permissions
    const defaultPerms = DEFAULT_STORAGE_PERMISSIONS[roleId];
    if (defaultPerms) {
      console.log(`[permissionService] Using DEFAULT_STORAGE_PERMISSIONS for role: ${roleId}`);
      return defaultPerms;
    }

    return null;
  } catch (error) {
    console.error('Error getting role storage permissions:', error);
    return null;
  }
};

/**
 * Update storage permissions for a role
 */
export const updateRoleStoragePermissions = async (
  roleId: UserRole,
  storagePermissions: StoragePermissions,
  updatedBy?: string
): Promise<void> => {
  try {
    const existingPermissions = await getRolePermissions(roleId);

    const rolePermissionsData: RolePermissions = {
      roleId,
      permissions: existingPermissions?.permissions || [],
      hierarchicalPermissions: existingPermissions?.hierarchicalPermissions,
      storagePermissions,
      menuAccess: existingPermissions?.menuAccess || [],
      customSettings: existingPermissions?.customSettings || {
        dataAccessLevel: DataAccessLevel.OWN,
        canExport: false,
        canBulkEdit: false,
        canDeletePermanently: false,
      },
      updatedAt: Timestamp.now(),
      updatedBy,
    };

    if (!existingPermissions) {
      rolePermissionsData.createdAt = Timestamp.now();
    }

    const docRef = doc(db, ROLE_PERMISSIONS_COLLECTION, roleId);
    await setDoc(docRef, rolePermissionsData);

    // Log the change if updatedBy is provided
    if (updatedBy) {
      await logPermissionChangeEntry({
        userId: updatedBy,
        action: 'update_storage_permissions',
        targetType: 'role',
        targetId: roleId,
        previousValue: JSON.stringify(existingPermissions?.storagePermissions || {}),
        newValue: JSON.stringify(storagePermissions),
        reason: 'Storage permission update via editor',
      });
    }

    // Clear cache
    rolePermissionsCache.delete(roleId);
  } catch (error) {
    console.error('Error updating role storage permissions:', error);
    throw error;
  }
};

/**
 * Get all roles' storage permissions
 */
export const getAllRoleStoragePermissions = async (): Promise<
  Record<string, StoragePermissions>
> => {
  const result: Record<string, StoragePermissions> = {};

  // First, get default permissions for all active roles
  Object.entries(DEFAULT_STORAGE_PERMISSIONS).forEach(([roleId, perms]) => {
    result[roleId] = perms;
  });

  // Then fetch any custom permissions from Firestore and merge
  try {
    const snapshot = await getDocs(collection(db, ROLE_PERMISSIONS_COLLECTION));

    snapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data() as RolePermissions;
      if (data.storagePermissions) {
        result[data.roleId] = data.storagePermissions;
      }
    });
  } catch (error) {
    console.error('Error fetching all role storage permissions:', error);
  }

  return result;
};